/* eslint-disable react/no-unknown-property -- Firebolt consumes the custom css prop at build time. */
import { css } from '@firebolt-dev/css'
import { useEffect, useMemo, useState } from 'react'
import { BookmarkIcon, CalendarDaysIcon, CheckIcon, CoinsIcon, ListChecksIcon, LockIcon, RefreshCwIcon } from 'lucide-react'

import { COLORS, ICON_SIZES, MOTION, RADII, SHADOWS, TYPE, space } from '../theme'
import { claimQuest, playerWallet, questJournal, refreshScreens, screenData, subscribeScreenData, worldStatus } from '../screen-data'
import {
  QUEST_JOURNAL_TABS,
  firstPopulatedQuestTab,
  normalizeQuestJournal,
  questProgress,
  questTabCounts,
  questsForTab,
} from '../quest-journal'
import { Btn, MetaStat, Panel, ScreenShell } from './VoiceKit'

export function QuestJournalScreen({ world, onClose }) {
  const [quests, setQuests] = useState(() => normalizeQuestJournal(questJournal(world)))
  const [loaded, setLoaded] = useState(() => screenData(world, 'quests') !== null)
  const [wallet, setWallet] = useState(() => playerWallet(world))
  const [tab, setTab] = useState(() => firstPopulatedQuestTab(normalizeQuestJournal(questJournal(world))))
  const [selectedId, setSelectedId] = useState(null)
  const [pendingClaim, setPendingClaim] = useState(null)

  useEffect(() => {
    const offQuests = subscribeScreenData(world, 'quests', value => {
      const next = normalizeQuestJournal(value)
      setQuests(next)
      setLoaded(true)
      setPendingClaim(current => (next.some(item => item.id === current && item.claimed) ? null : current))
    })
    const offPlayer = subscribeScreenData(world, 'player', value => setWallet(value && typeof value === 'object' ? value : playerWallet(world)))
    refreshScreens(world)
    return () => {
      offQuests()
      offPlayer()
    }
  }, [world])

  useEffect(() => {
    const onKey = event => {
      if (event.key === 'Escape') onClose?.()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const counts = useMemo(() => questTabCounts(quests), [quests])
  const visible = useMemo(() => questsForTab(quests, tab), [quests, tab])
  const selected = visible.find(item => item.id === selectedId) || visible[0] || null
  const status = worldStatus(world)

  useEffect(() => {
    if (visible.length && !visible.some(item => item.id === selectedId)) setSelectedId(visible[0].id)
  }, [visible, selectedId])

  const handleClaim = quest => {
    if (!quest?.done || quest.claimed || pendingClaim) return
    if (!claimQuest(world, quest.id)) return
    setPendingClaim(quest.id)
    setTimeout(() => refreshScreens(world), 500)
  }

  return (
    <ScreenShell
      label='WORLD'
      title='ДНЕВНИК ЗАДАНИЙ'
      subtitle='Цели и награды приходят из живого состояния мира'
      ariaLabel='Дневник заданий'
      onClose={onClose}
      meta={
        <>
          <MetaStat icon={<BookmarkIcon size={ICON_SIZES.sm} color={COLORS.cyan} />} label='МЕСТО' value={status.place} />
          <MetaStat icon={<CoinsIcon size={ICON_SIZES.sm} color={COLORS.warning} />} label='БАЛАНС' value={`${wallet.balance || 0} МК`} />
        </>
      }
    >
      <div
        css={css`
          flex: 1 1 auto;
          min-height: 0;
          display: grid;
          grid-template-columns: 220px minmax(320px, 1fr) minmax(320px, 420px);
          gap: ${space(2)};
          padding: ${space(2)};
          overflow: hidden;
          @media (max-width: 1050px) {
            grid-template-columns: 180px 1fr;
            overflow-y: auto;
          }
          @media (max-width: 680px) {
            grid-template-columns: 1fr;
            padding: ${space(1)};
          }
        `}
      >
        <nav
          aria-label='Разделы дневника'
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${space(0)};
            @media (max-width: 680px) {
              flex-direction: row;
              overflow-x: auto;
            }
          `}
        >
          {QUEST_JOURNAL_TABS.map(item => (
            <button
              key={item.id}
              type='button'
              aria-current={tab === item.id ? 'page' : undefined}
              onClick={() => {
                setTab(item.id)
                setSelectedId(null)
              }}
              css={css`
                min-height: 44px;
                padding: 0 ${space(1)};
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: ${space(1)};
                border: 1px solid ${tab === item.id ? COLORS.borderStrong : 'transparent'};
                border-radius: ${RADII.lg};
                background: ${tab === item.id ? COLORS.cyanSoft : 'transparent'};
                color: ${tab === item.id ? COLORS.cyan : COLORS.textSecondary};
                font: inherit;
                font-size: ${TYPE.caption.size};
                font-weight: 650;
                letter-spacing: 0.06em;
                cursor: pointer;
                white-space: nowrap;
                &:focus-visible { outline: none; box-shadow: ${SHADOWS.focus}; }
              `}
            >
              <span>{item.label}</span>
              <span>{counts[item.id]}</span>
            </button>
          ))}
          <Btn full onClick={() => refreshScreens(world)}>
            <RefreshCwIcon size={ICON_SIZES.xs} /> ОБНОВИТЬ
          </Btn>
        </nav>

        <Panel title={`${QUEST_JOURNAL_TABS.find(item => item.id === tab)?.label || 'Задания'} · ${visible.length}`}>
          <div
            css={css`
              min-height: 0;
              max-height: calc(100vh - 150px);
              overflow-y: auto;
              display: flex;
              flex-direction: column;
              gap: ${space(1)};
            `}
          >
            {!loaded ? (
              <EmptyState icon={<RefreshCwIcon size={ICON_SIZES.lg} />} title='СИНХРОНИЗАЦИЯ' text='Запрашиваем актуальный журнал у мира.' />
            ) : visible.length === 0 ? (
              <EmptyState icon={<ListChecksIcon size={ICON_SIZES.lg} />} title='ЗДЕСЬ ПОКА ПУСТО' text='Мир не прислал заданий для этого раздела.' />
            ) : (
              visible.map(quest => (
                <QuestRow key={quest.id} quest={quest} selected={selected?.id === quest.id} onClick={() => setSelectedId(quest.id)} />
              ))
            )}
          </div>
        </Panel>

        <div
          css={css`
            min-height: 0;
            @media (max-width: 1050px) { grid-column: 1 / -1; }
          `}
        >
          <Panel title='ДЕТАЛИ И НАГРАДА'>
            {selected ? (
              <QuestDetails quest={selected} claiming={pendingClaim === selected.id} onClaim={() => handleClaim(selected)} />
            ) : (
              <EmptyState icon={<BookmarkIcon size={ICON_SIZES.lg} />} title='ВЫБЕРИ ЗАДАНИЕ' text='Здесь появятся реальные шаги и награда.' />
            )}
          </Panel>
        </div>
      </div>
    </ScreenShell>
  )
}

function QuestRow({ quest, selected, onClick }) {
  const progress = questProgress(quest)
  return (
    <button
      type='button'
      onClick={onClick}
      css={css`
        appearance: none;
        width: 100%;
        padding: ${space(2)};
        display: grid;
        gap: ${space(1)};
        text-align: left;
        border: 1px solid ${selected ? COLORS.borderStrong : COLORS.border};
        border-radius: ${RADII.xl};
        background: ${selected ? COLORS.cyanSoft : COLORS.bgPanelLight};
        color: ${COLORS.text};
        cursor: pointer;
        transition: border-color ${MOTION.hover}, transform ${MOTION.hover};
        &:hover { border-color: ${COLORS.borderStrong}; transform: translateY(-1px); }
        &:focus-visible { outline: none; box-shadow: ${SHADOWS.focus}; }
      `}
    >
      <div css={css`display:flex;align-items:center;gap:${space(1)};`}>
        {quest.daily ? <CalendarDaysIcon size={ICON_SIZES.xs} color={COLORS.warning} /> : quest.locked ? <LockIcon size={ICON_SIZES.xs} /> : <ListChecksIcon size={ICON_SIZES.xs} color={COLORS.cyan} />}
        <strong css={css`font-size:${TYPE.body.size};flex:1;`}>{quest.name}</strong>
        <span css={css`font-size:${TYPE.caption.size};color:${COLORS.textSecondary};`}>{quest.doneSteps}/{quest.totalSteps}</span>
      </div>
      {quest.hint && <span css={css`font-size:${TYPE.caption.size};color:${COLORS.textSecondary};`}>{quest.hint}</span>}
      <Progress value={progress} done={quest.done} />
    </button>
  )
}

function QuestDetails({ quest, claiming, onClaim }) {
  const credits = Math.max(0, Number(quest.reward?.credits) || 0)
  return (
    <div css={css`display:flex;flex-direction:column;gap:${space(2)};`}>
      <div>
        <div css={css`font-size:${TYPE.section.size};font-weight:${TYPE.section.weight};letter-spacing:${TYPE.section.spacing};color:${COLORS.cyan};`}>{quest.name}</div>
        {quest.hint && <p css={css`margin:${space(1)} 0 0;color:${COLORS.textSecondary};font-size:${TYPE.body.size};`}>{quest.hint}</p>}
      </div>
      <Progress value={questProgress(quest)} done={quest.done} />
      <div css={css`display:flex;flex-direction:column;gap:${space(1)};`}>
        {quest.steps.map(step => (
          <div key={step.id || step.text} css={css`display:flex;align-items:flex-start;gap:${space(1)};font-size:${TYPE.body.size};color:${step.done ? COLORS.textSecondary : COLORS.text};`}>
            <span css={css`width:20px;height:20px;flex:none;border-radius:${RADII.round};display:grid;place-items:center;border:1px solid ${step.done ? COLORS.success : COLORS.border};background:${step.done ? COLORS.successSoft : 'transparent'};`}>
              {step.done && <CheckIcon size={12} color={COLORS.success} />}
            </span>
            <span>{step.text}</span>
          </div>
        ))}
      </div>
      <div css={css`padding:${space(2)};display:flex;align-items:center;justify-content:space-between;border:1px solid ${COLORS.border};border-radius:${RADII.lg};background:${COLORS.bgPanelLight};`}>
        <span css={css`font-size:${TYPE.caption.size};letter-spacing:.12em;color:${COLORS.textSecondary};`}>НАГРАДА</span>
        <strong css={css`display:flex;align-items:center;gap:${space(0)};color:${COLORS.warning};`}><CoinsIcon size={ICON_SIZES.xs} /> {credits} МК</strong>
      </div>
      {quest.done && !quest.claimed && <Btn full variant='success' disabled={claiming} onClick={onClaim}>{claiming ? 'ПОЛУЧАЕМ…' : 'ЗАБРАТЬ НАГРАДУ'}</Btn>}
      {quest.claimed && <div role='status' css={css`padding:${space(1)};text-align:center;color:${COLORS.success};background:${COLORS.successSoft};border-radius:${RADII.lg};font-size:${TYPE.caption.size};font-weight:700;`}>НАГРАДА ПОЛУЧЕНА</div>}
      {quest.locked && <div css={css`display:flex;gap:${space(1)};color:${COLORS.textSecondary};font-size:${TYPE.caption.size};`}><LockIcon size={ICON_SIZES.xs} /> Задание ещё не открыто условиями мира.</div>}
    </div>
  )
}

function Progress({ value, done }) {
  return <div aria-label={`Прогресс ${Math.round(value * 100)}%`} css={css`height:5px;overflow:hidden;border-radius:${RADII.round};background:${COLORS.disabled};`}><div css={css`width:${Math.round(value * 100)}%;height:100%;background:${done ? COLORS.success : COLORS.cyan};transition:width ${MOTION.panel};`} /></div>
}

function EmptyState({ icon, title, text }) {
  return <div role='status' css={css`min-height:180px;display:grid;place-items:center;align-content:center;gap:${space(1)};text-align:center;color:${COLORS.textSecondary};`}><span>{icon}</span><strong css={css`color:${COLORS.text};letter-spacing:.12em;`}>{title}</strong><span css={css`font-size:${TYPE.caption.size};max-width:280px;`}>{text}</span></div>
}
